import { config } from '../utils/config.js'
import deviceManager from './DeviceManager.js'

export const PAYMENT_METHODS = {
  CARD: 'card',
  QR_CODE: 'qr_code',
  MOBILE_REDIRECT: 'mobile_redirect',
  CASH: 'cash'
}

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  TIMEOUT: 'timeout'
}

export class PaymentService {
  constructor(configManager) {
    this.baseUrl = `${config.getApiUrl()}/api/v1`
    this.configManager = configManager
    this.authToken = config.getAuthToken()
    this.currentPayment = null
    this.pollingInterval = null
    this.listeners = new Set()

    this.isTablet = this.detectTabletEnvironment()
    this.paymentTimeout = this.isTablet ? 300000 : 180000
    this.pollingFrequency = 5000
    this.digitalReceiptPreference = null
  }

  setDigitalReceiptPreference(pref) {
    this.digitalReceiptPreference = pref || null
  }

  detectTabletEnvironment() {
    const userAgent = navigator.userAgent
    const isAndroidTablet = /Android.*(?!Mobile)/.test(userAgent)
    const isIPadTablet = /iPad/.test(userAgent)
    const isWebView = /wv|WebView/.test(userAgent)
    const hasAndroidDevice = typeof window.AndroidDevice !== 'undefined'

    return isAndroidTablet || isIPadTablet || isWebView || hasAndroidDevice
  }

  subscribe(listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  notifyListeners(status, data) {
    this.listeners.forEach(listener => {
      try {
        listener(status, data)
      } catch (error) {
        console.error('Payment listener error:', error)
      }
    })
  }

  async getAvailablePaymentMethods() {
    try {
      console.log('[Payment] Getting available payment methods from restaurant config')

      const config = this.configManager.getCurrentConfig()
      const acceptedMethods = config?.orderingConfiguration?.acceptedPaymentMethods || []

      if (acceptedMethods.length > 0) {
        console.log('[Payment] Restaurant accepted payment methods:', acceptedMethods)
        const mapped = this.transformPaymentMethodsFromConfig(acceptedMethods)
        if (Object.keys(mapped).length > 0) return mapped
      }

      const flow = config?.paymentFlowConfiguration?.paymentMethods
      if (flow) {
        const list = Array.isArray(flow) ? flow : []
        const get = (method) => list.find(m => (m.method || '').toUpperCase() === method)?.enabled

        const m = {}
        if (get('QR_CODE')) {
          m.qr_code = {
            id: PAYMENT_METHODS.QR_CODE,
            name: 'Scan QR Code',
            icon: 'fas fa-qrcode',
            enabled: true,
          }
        }
        if (get('BLIK')) {
          m.blik = {
            id: 'blik',
            name: 'BLIK Code',
            icon: 'fas fa-key',
            enabled: true,
          }
        }
        if (get('DESK_PAYMENT') || get('STAFF_PAYMENT')) {
          m.cash = {
            id: PAYMENT_METHODS.CASH,
            name: 'Pay at Counter',
            icon: 'fas fa-money-bill',
            enabled: true,
          }
        }
        if (Object.keys(m).length === 0) {
          m.card = {
            id: PAYMENT_METHODS.CARD,
            name: 'Credit/Debit Card',
            icon: 'fas fa-credit-card',
            enabled: true,
          }
        }
        return m
      }

      return this.getDefaultPaymentMethods()
    } catch (error) {
      console.error('[Payment] Failed to get payment methods from config:', error)
      return this.getDefaultPaymentMethods()
    }
  }

  transformPaymentMethodsFromConfig(acceptedMethods) {
    const methodMap = {
      'CASH': PAYMENT_METHODS.CASH,
      'CREDIT_CARD': PAYMENT_METHODS.CARD,
      'DEBIT_CARD': PAYMENT_METHODS.CARD,
      'QR_CODE': PAYMENT_METHODS.QR_CODE,
      'MOBILE_PAYMENT': PAYMENT_METHODS.MOBILE_REDIRECT
    }

    const availableMethods = {}

    acceptedMethods.forEach(method => {
      const mappedMethod = methodMap[method]
      if (mappedMethod) {
        switch (mappedMethod) {
          case PAYMENT_METHODS.CARD:
            availableMethods.card = {
              id: PAYMENT_METHODS.CARD,
              name: 'Credit/Debit Card',
              icon: 'fas fa-credit-card',
              enabled: true,
              processingFee: 0,
              supportedCards: ['visa', 'mastercard', 'amex']
            }
            break
          case PAYMENT_METHODS.QR_CODE:
            availableMethods.qr_code = {
              id: PAYMENT_METHODS.QR_CODE,
              name: 'Scan QR Code',
              icon: 'fas fa-qrcode',
              enabled: true,
              providers: ['blik', 'google_pay', 'apple_pay']
            }
            break
          case PAYMENT_METHODS.MOBILE_REDIRECT:
            availableMethods.mobile_redirect = {
              id: PAYMENT_METHODS.MOBILE_REDIRECT,
              name: 'Pay on Phone',
              icon: 'fas fa-mobile-alt',
              enabled: true
            }
            break
          case PAYMENT_METHODS.CASH:
            availableMethods.cash = {
              id: PAYMENT_METHODS.CASH,
              name: 'Pay at Counter',
              icon: 'fas fa-money-bill',
              enabled: true
            }
            break
        }
      }
    })

    return availableMethods
  }

  getDefaultPaymentMethods() {
    return {
      card: {
        id: PAYMENT_METHODS.CARD,
        name: 'Credit/Debit Card',
        icon: 'fas fa-credit-card',
        enabled: true,
        processingFee: 0,
        supportedCards: ['visa', 'mastercard', 'amex']
      },
      cash: {
        id: PAYMENT_METHODS.CASH,
        name: 'Pay at Counter',
        icon: 'fas fa-money-bill',
        enabled: true
      }
    }
  }

  async initiatePayment(orderData, paymentMethod, amount) {
    try {
      const restaurantId = this.configManager.getCurrentRestaurantId()
      const deviceId = deviceManager.getDeviceId()

      const paymentPayload = {
        orderId: orderData.id,
        paymentMethod,
        providerType: this.mapProviderForMethod(paymentMethod),
        amount: parseFloat(amount),
        currency: orderData.currency || 'PLN',
        deviceId,
        customerInfo: orderData.customerInfo || {},
        metadata: {
          orderType: orderData.type || 'dine-in',
          deviceType: this.isTablet ? 'tablet' : 'web',
          timestamp: new Date().toISOString(),
          digitalReceipt: {
            email: this.digitalReceiptPreference?.email || '',
            phone: this.digitalReceiptPreference?.phone || '',
            consent: !!this.digitalReceiptPreference?.consent,
          }
        }
      }

      const tokenPreview = (this.authToken || '').slice(0, 8)
      console.log('[Payment] Initiating payment:', { ...paymentPayload, tokenPreview })

      const response = await fetch(`${this.baseUrl}/device/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
          'X-Device-Id': deviceId,
        },
        body: JSON.stringify(paymentPayload)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const paymentResult = await response.json()
      console.log('[Payment] Payment initiated:', paymentResult)

      if (!paymentResult?.success || !paymentResult?.transactionId) {
        const errMsg = paymentResult?.message || 'Payment creation failed'
        throw new Error(errMsg)
      }

      this.currentPayment = {
        id: paymentResult.transactionId,
        method: paymentMethod,
        amount,
        status: PAYMENT_STATUS.PENDING,
        createdAt: Date.now(),
        ...paymentResult
      }

      if (paymentMethod !== PAYMENT_METHODS.CASH) {
        this.startStatusPolling()
      }

      this.notifyListeners(PAYMENT_STATUS.PENDING, this.currentPayment)

      return this.currentPayment
    } catch (error) {
      console.error('[Payment] Failed to initiate payment:', error)
      this.notifyListeners(PAYMENT_STATUS.FAILED, { error: error.message })
      throw error
    }
  }

  mapProviderForMethod(paymentMethod) {
    const method = (paymentMethod || '').toLowerCase()
    switch (method) {
      case 'qr_code':
      case 'blik':
        return 'TPAY'
      default:
        return undefined
    }
  }

  async initiateGroupPayment(orderIds, paymentMethod) {
    try {
      const restaurantId = this.configManager.getCurrentRestaurantId()
      const deviceId = deviceManager.getDeviceId()

      const payload = {
        orderIds,
        paymentMethod,
        deviceId,
        metadata: {
          group: true,
          count: orderIds.length,
          deviceType: this.isTablet ? 'tablet' : 'web',
          timestamp: new Date().toISOString()
        }
      }

      const response = await fetch(`${this.baseUrl}/device/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
          'X-Device-Id': deviceId,
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const paymentResult = await response.json()

      this.currentPayment = {
        id: paymentResult.transactionId,
        method: paymentMethod,
        status: PAYMENT_STATUS.PENDING,
        createdAt: Date.now(),
        ...paymentResult
      }

      this.startStatusPolling()
      this.notifyListeners(PAYMENT_STATUS.PENDING, this.currentPayment)
      return this.currentPayment
    } catch (error) {
      console.error('[Payment] Failed to initiate group payment:', error)
      this.notifyListeners(PAYMENT_STATUS.FAILED, { error: error.message })
      throw error
    }
  }

  startStatusPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
    }

    const startTime = Date.now()

    this.pollingInterval = setInterval(async () => {
      try {
        if (Date.now() - startTime > this.paymentTimeout) {
          console.warn('[Payment] Payment timeout reached')
          this.stopStatusPolling()
          this.notifyListeners(PAYMENT_STATUS.TIMEOUT, this.currentPayment)
          return
        }

        const status = await this.checkPaymentStatus()

        if (status && (status.status || status.paidAt || status.isExpired || status.expiresAt)) {
          let mapped = this.mapBackendStatus(status.status)
          try {
            const now = Date.now()
            const expiresAtMs = status?.expiresAt ? Date.parse(status.expiresAt) : null
            if (mapped === PAYMENT_STATUS.PENDING && status?.paidAt) {
              mapped = PAYMENT_STATUS.COMPLETED
            } else if (mapped === PAYMENT_STATUS.PENDING && (status?.isExpired || (expiresAtMs && expiresAtMs < now))) {
              mapped = PAYMENT_STATUS.TIMEOUT
            }
          } catch {}
          if (mapped !== this.currentPayment?.status) {
            console.log('[Payment] Status changed:', status.status, '->', mapped)
            this.currentPayment = { ...this.currentPayment, ...status, status: mapped }
            this.notifyListeners(mapped, this.currentPayment)

            if ([PAYMENT_STATUS.COMPLETED, PAYMENT_STATUS.FAILED, PAYMENT_STATUS.CANCELLED, PAYMENT_STATUS.TIMEOUT].includes(mapped)) {
              this.stopStatusPolling()
            }
          }
        }
      } catch (error) {
        console.error('[Payment] Status polling error:', error)
      }
    }, this.pollingFrequency)

    console.log('[Payment] Started status polling')
  }

  stopStatusPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
      console.log('[Payment] Stopped status polling')
    }
  }

  async checkPaymentStatus() {
    if (!this.currentPayment?.id) {
      return null
    }

    try {
      const restaurantId = this.configManager.getCurrentRestaurantId()
      const deviceId = deviceManager.getDeviceId()

      const cacheBust = Date.now()
      const response = await fetch(`${this.baseUrl}/device/payments/status/${this.currentPayment.id}?_=${cacheBust}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
          'X-Device-Id': deviceId,
          'Cache-Control': 'no-cache',
        }
      })

      if (response.status === 304) {
        return { status: this.currentPayment?.status }
      }

      if (!response.ok) {
        let message = `HTTP ${response.status}: ${response.statusText}`
        try {
          const data = await response.json()
          if (data?.message) message = data.message
        } catch {}

        if (message.toLowerCase().includes('different restaurant')) {
          const tokenPreview = (this.authToken || '').slice(0, 8)
          console.warn('[Payment] Stopping polling due to device/payment restaurant mismatch', {
            transactionId: this.currentPayment?.id,
            deviceId,
            tokenPreview
          })
          this.stopStatusPolling()
          this.notifyListeners(PAYMENT_STATUS.FAILED, { error: 'restaurant_mismatch' })
          return null
        }
        throw new Error(message)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('[Payment] Failed to check status:', error)
      return null
    }
  }

  mapBackendStatus(raw) {
    const s = (raw || '').toString().toLowerCase()
    switch (s) {
      case 'paid':
      case 'completed':
      case 'success':
      case 'succeeded':
      case 'confirmed':
      case 'approved':
      case 'authorized':
      case 'settled':
      case 'paid_out':
        return PAYMENT_STATUS.COMPLETED
      case 'failed':
      case 'error':
      case 'declined':
        return PAYMENT_STATUS.FAILED
      case 'cancelled':
      case 'canceled':
        return PAYMENT_STATUS.CANCELLED
      case 'expired':
      case 'timeout':
        return PAYMENT_STATUS.TIMEOUT
      case 'processing':
      case 'in_progress':
        return PAYMENT_STATUS.PROCESSING
      case 'pending':
      default:
        return PAYMENT_STATUS.PENDING
    }
  }

  async cancelPayment(reason = 'user_cancelled') {
    if (!this.currentPayment?.id) {
      return false
    }

    try {
      const restaurantId = this.configManager.getCurrentRestaurantId()
      const deviceId = deviceManager.getDeviceId()

      console.log('[Payment] Cancelling payment:', this.currentPayment.id)

      const response = await fetch(`${this.baseUrl}/device/payments/${this.currentPayment.id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${this.authToken}`,
          'X-Device-Id': deviceId,
        },
        body: JSON.stringify({ reason })
      })

      if (response.ok) {
        this.stopStatusPolling()
        this.currentPayment.status = PAYMENT_STATUS.CANCELLED
        this.notifyListeners(PAYMENT_STATUS.CANCELLED, this.currentPayment)
        console.log('[Payment] Payment cancelled successfully')
        return true
      }

      return false
    } catch (error) {
      console.error('[Payment] Failed to cancel payment:', error)
      return false
    }
  }

  getCurrentPayment() {
    return this.currentPayment
  }

  resetPayment() {
    this.stopStatusPolling()
    this.currentPayment = null
    console.log('[Payment] Payment state reset')
  }

  async handleTabletPayment(orderData, paymentMethod, amount) {
    console.log('[Payment] Handling tablet payment flow', { paymentMethod, amount })

    try {
      if (deviceManager.isAndroid()) {
        deviceManager.logEvent('payment_initiated', {
          method: paymentMethod,
          amount,
          deviceId: deviceManager.getDeviceId(),
          timestamp: new Date().toISOString()
        })
      }

      const payment = await this.initiatePayment(orderData, paymentMethod, amount)

      switch (paymentMethod) {
        case PAYMENT_METHODS.CARD:
          return this.handleCardPayment(payment)
        case PAYMENT_METHODS.QR_CODE:
          return this.handleQRCodePayment(payment)
        case PAYMENT_METHODS.MOBILE_REDIRECT:
          return this.handleMobileRedirectPayment(payment)
        case PAYMENT_METHODS.CASH:
          return this.handleCashPayment(payment)
        default:
          throw new Error(`Unsupported payment method: ${paymentMethod}`)
      }
    } catch (error) {
      console.error('[Payment] Tablet payment failed:', error)
      throw error
    }
  }

  async handleCardPayment(payment) {
    console.log('[Payment] Starting card payment flow')

    if (this.isTablet && deviceManager.isAndroid()) {
      try {
        if (window.AndroidDevice && window.AndroidDevice.initiateCardPayment) {
          await window.AndroidDevice.initiateCardPayment(payment.id, payment.amount)
        }
      } catch (error) {
        console.warn('[Payment] Android card reader not available:', error)
      }
    }

    return payment
  }

  async handleQRCodePayment(payment) {
    console.log('[Payment] Starting QR code payment flow')
    return payment
  }

  async handleMobileRedirectPayment(payment) {
    console.log('[Payment] Starting mobile redirect payment flow')

    if (payment.redirectUrl) {
      if (this.isTablet) {
        console.log('[Payment] Generating QR code for mobile redirect')
        return { ...payment, showQRCode: true }
      } else {
        window.location.href = payment.redirectUrl
      }
    }

    return payment
  }

  async handleCashPayment(payment) {
    console.log('[Payment] Cash payment selected - no processing required')

    this.currentPayment.status = PAYMENT_STATUS.COMPLETED
    this.notifyListeners(PAYMENT_STATUS.COMPLETED, this.currentPayment)

    return this.currentPayment
  }

  cleanup() {
    this.stopStatusPolling()
    this.listeners.clear()
    this.currentPayment = null
    console.log('[Payment] Service cleaned up')
  }
}
